import {
  ExpressionValue,
  type ExpressionValueFactoryType,
} from '../models/project.model';
import { isNumeric, removeNumberFormatting } from './math.helpers';
import { hasDefinedProperties, isPrimitive } from './object.helpers';
import { isObject } from 'lodash';
import { validateFiniteNumber } from '../validation/number.validation';
import { guards } from './typeguards.helpers';
import { getDisplayNumber } from './display/get-display-number.helper';

export type { ExpressionValueFactoryType } from '../models/project.model';

/**
 * Test if value is valid values to create a new ExpressionValue
 */
export const isExpressionValueFactoryType = (
  value: unknown,
): value is ExpressionValueFactoryType =>
  (typeof value === 'number' && isFinite(value)) ||
  typeof value === 'string' ||
  isExpressionValue(value);

/**
 * Create a new ExpressionValue based on a
 * number string or preexisting ExpressionValue
 */
export const createExpression = (
  value: ExpressionValueFactoryType = 0,
  resolvedValue = 0,
): ExpressionValue => {
  // Expression already exist so use a clone of that
  if (isExpressionValue(value)) {
    return { ...value, formatted: getDisplayNumber(value.resolved) };
  }

  // String or number
  if (isPrimitive(value)) {
    // Numeric values like '12' or 12
    if (isNumeric(value)) {
      return {
        expression: String(value),
        resolved: +value,
        formatted: getDisplayNumber(+value),
      };
    }
    // Value is a string expression like 'a + b'
    else {
      return {
        expression: String(value),
        resolved: resolvedValue,
        formatted: getDisplayNumber(resolvedValue),
      };
    }
  }

  const inFormatted = value.formatted;
  const numericFormatted = inFormatted
    ? validateFiniteNumber(+removeNumberFormatting(inFormatted))
    : undefined;
  const inResolved = guards.is.number(value.resolved)
    ? validateFiniteNumber(value.resolved)
    : undefined;
  const inExpression = value.expression;

  const expression = String(
    inExpression ?? inResolved ?? numericFormatted ?? '',
  );
  const numericExpression = isNumeric(expression) ? +expression : undefined;
  const resolved =
    value.value ??
    inResolved ??
    numericExpression ??
    numericFormatted ??
    resolvedValue;
  const formatted =
    inFormatted ??
    getDisplayNumber(
      value.value ?? inResolved ?? numericExpression ?? resolvedValue,
    );

  return {
    value: value.value, // Important to keep value.value even if undefined to be able to remove value
    expression,
    formatted,
    resolved,
  };
};

/**
 * Unwrap any numbers or strings to create a new ExpressionValue
 * @param countFactory
 * @param allowUndefinedCount
 * @returns
 */
export const resolveFactoryCount = <
  T extends {
    count?: ExpressionValueFactoryType;
    fallbackCount?: ExpressionValueFactoryType;
  },
  R = Omit<T, 'count'> & { count: ExpressionValue },
>(
  countFactory: T,
  allowUndefinedCount = false,
): R => {
  if (
    typeof countFactory.count === 'boolean' ||
    typeof countFactory.fallbackCount === 'boolean'
  ) {
    return countFactory as unknown as R;
  }
  if (isExpressionValue(countFactory.count)) {
    return countFactory as unknown as R;
  }
  if (
    countFactory.count === undefined &&
    countFactory.fallbackCount !== undefined
  ) {
    return {
      ...countFactory,
      fallbackCount: createExpression(countFactory.fallbackCount),
    } as R;
  }
  // If count is undefined and we allow undefined count
  if (
    allowUndefinedCount &&
    'count' in countFactory &&
    countFactory.count === undefined
  ) {
    return { ...countFactory, count: undefined } as R;
  }
  return { ...countFactory, count: createExpression(countFactory.count) } as R;
};

/**
 * Create an empty ExpressionValue
 */
export const createEmptyExpression = (): ExpressionValue => ({
  expression: '',
  formatted: '',
  resolved: 0,
});

/**
 * Test if value is an ExpressionValue
 * @param value
 */
export const isExpressionValue = (value: unknown): value is ExpressionValue =>
  isObject(value) &&
  hasDefinedProperties(
    value as ExpressionValue,
    'expression',
    'formatted',
    'resolved',
  );

/**
 * Test if an expression is empty (undefined or has no value and empty expression string)
 * @param expressionValue
 */
export const isEmptyExpressionValue = (
  expressionValue: ExpressionValue,
): boolean => {
  const hasNoValue = guards.is.undefined(expressionValue.value);
  const hasNoExpression = guards.is.emptyString(expressionValue.expression);
  return hasNoValue && hasNoExpression;
};
